import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { FaCloudUploadAlt, FaUndo } from "react-icons/fa";
import { motion } from "framer-motion";
import { AiFillCheckCircle } from "react-icons/ai";
import { GiCancel } from "react-icons/gi";
import { toast, ToastContainer } from "react-toastify";
import { GrPowerReset } from "react-icons/gr";
export default function FileUploader({
    multiple = false,
    progressBarColor = "#4a69dd",
    allowedFormats = ["image/png", "image/jpeg", "application/pdf"],
    maxFileSize = 10 * 1024 * 1024,
    uploadText = "Drag and drop to upload files",
    maxFiles = 10,
    files,
    setFiles
}) {
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [isUploaded, setIsUploaded] = useState(false);
    const [isLimitReached, setIsLimitReached] = useState(false);
    const [uploading_files, setUploading_files] = useState(0);
    const [pdfName, setPdfName] = useState("");

    const onDrop = useCallback(
        (acceptedFiles) => {
            if (isLimitReached) return;
                    if (allowedFormats.includes("image/png") || allowedFormats.includes("image/jpeg")) {
                        const maxImageSize = 3 * 1024 * 1024; 
                        if (!multiple) {
                            // Single upload
                            if (acceptedFiles[0].size > maxImageSize) {
                                toast.error("Image size should not exceed 3MB")
                                return
                            }
                        } else {
                            // Multiple upload
                            const oversizedFiles = acceptedFiles.filter(file => file.size > maxImageSize)
                            if (oversizedFiles.length > 0) {
                                toast.error("All images must be under 3MB in size")
                                return
                            }
                        }
                    }            if (files.length + acceptedFiles.length > maxFiles) {
                toast.warning(`You can only upload up to ${maxFiles} files.`)
                return;
            }
            startUpload([...acceptedFiles]);
        },
        [files, isUploaded, maxFiles]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        multiple,
        accept: allowedFormats.reduce((acc, type) => {
            acc[type] = [];
            return acc;
        }, {}),
        maxSize: maxFileSize,
        disabled: isUploaded && files.length >= maxFiles,
    });

    const startUpload = (fileList) => {
        if (allowedFormats == "application/pdf") {
            setPdfName(fileList[0].name)
        }
        setUploading_files(fileList.length)
        setIsUploaded(false)
        setUploading(true);
        let totalSize = fileList.reduce((acc, file) => acc + file.size, 0);
        let uploaded = 0;

        const interval = setInterval(() => {
            uploaded += totalSize / 100;
            let percentage = Math.min((uploaded / totalSize) * 100, 100);
            setProgress(percentage);
            if (percentage >= 100) {
                clearInterval(interval);
                setFiles([...files, ...fileList.map(file => ({ id: crypto.randomUUID(), file }))]);
                setUploading(false);
                setTimeout(() => {
                    setIsUploaded(true);
                    setUploading_files(0)
                    setProgress(0);
                }, 2000);
            }
        }, 10);
    };

    const handleReset = () => {
        if (allowedFormats == "application/pdf") {
            setPdfName("")
        }
        setUploading(false);
        setIsUploaded(false);
        setFiles([]);
        setProgress(0);
    };
    return (
        <>
            <div className="d-flex justify-content-center align-items-center ">
                <motion.div
                    className="bg-white p-4 rounded-4 shadow-sm "
                    style={{ maxWidth: "700px", width: "100%", border: "1px dashed rgb(162, 162, 162)" }}
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.5 }}
                >
                    <div
                        {...getRootProps()}
                        className={`border border-2 rounded-3 p-5 mb-3 d-flex flex-column align-items-center justify-content-center ${isDragActive ? "border-primary" : "border-dashed"
                            }`}
                        style={{
                            minHeight: "300px",
                            borderStyle: "dashed",
                            backgroundColor: isDragActive ? "rgba(13, 110, 253, 0.05)" : "transparent",
                            cursor: "pointer",
                        }}
                    >
                        <input id="upload_input" {...getInputProps()} />
                        {!allowedFormats.includes("application/pdf") ?
                            (<>
                                <p className="text-center mb-4 fw-medium" style={{ color: "#2c3e50" }}>
                                    {uploadText}
                                </p>

                                <button
                                    className={`btn btn-primary px-4 py-2 d-flex align-items-center gap-2`}
                                    style={{
                                        backgroundColor: "#4a69dd",
                                        borderColor: "#4a69dd",
                                        borderRadius: "30px",
                                    }}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (isUploaded) {
                                            handleReset();
                                        } else {
                                            document.getElementById("upload_input").click();
                                        }
                                    }}
                                >
                                    <FaCloudUploadAlt />
                                    <span>Browse Files</span>
                                </button>
                            </>) : (

                                <>
                                    <p className="text-center mb-4 fw-medium" style={{ color: "#2c3e50" }}>
                                        {isUploaded ? pdfName : uploadText}
                                    </p>

                                    <button
                                        className={`btn btn-${isUploaded ? "warning" : "primary"} px-4 py-2 d-flex align-items-center gap-2`}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (isUploaded) {
                                                handleReset();
                                            } else {
                                                document.getElementById("upload_input").click();
                                            }
                                        }}
                                    >

                                        {isUploaded ? <GrPowerReset /> : <FaCloudUploadAlt />}
                                        <span>{isUploaded ? "Reset" : "Browse Files"}</span>
                                    </button>

                                </>
                            )
                        }
                    </div>

                    {uploading_files > 0 && !isUploaded && (
                        <div className="bg-light p-3 rounded-3">

                            <div className="d-flex justify-content-between align-items-center mb-2">
                                {uploading ? <span className="text-muted">Uploading {uploading_files} file(s)..</span> : <span className="text-muted">Uploaded {uploading_files} file(s)</span>}
                                <div className="d-flex gap-2">
                                    {uploading ? (
                                        null
                                    ) : (
                                        <AiFillCheckCircle size={20} color="green" />
                                    )}
                                </div>
                            </div>
                            <div className="progress mt-2 position-relative" style={{ height: "8px" }}>
                                <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{
                                        width: `${progress}%`,
                                        backgroundColor: progressBarColor,
                                    }}
                                    aria-valuenow={progress}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                ></div>
                            </div>
                        </div>
                    )}
                </motion.div>
            </div>
        </>
    );
}
